import {
  Avatar,
  AvatarFallback,
  DropdownMenuGroup,
  DropdownMenuLabel,
  Icon,
  Sidebar,
  SidebarHeader,
  SidebarHeaderWorkspace,
  SidebarHeaderWorkspaceLogo,
  SidebarHeaderWorkspaceTitle,
  SidebarHeaderWorkspaceActions,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarFooter,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarDropdownMenu,
  SidebarDropdownMenuTrigger,
  SidebarDropdownMenuButton,
  SidebarDropdownMenuContent,
  SidebarDropdownMenuItem,
  DropdownMenuSeparator,
  SidebarUserProfile,
  SidebarUserProfileInfo,
  SidebarUserProfileName,
  SidebarUserProfileEmail,
  SidebarTrigger,
  useSidebar,
} from "@design-system/components";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useAuth, useImpersonation, getKeycloakInstance } from "@spiriidk/auth";
import {
  connectMenuItems,
  connectFooterMenuItems,
  connectHelpMenuItem,
  connectHelpMenuGroups,
  connectUserProfileMenuItems,
  FEATURE_FLAGS,
  SidebarCloseIcon,
} from "@spiriidk/connect-configs";
import {
  useFlag,
  type FeatureFlagConfiguration,
} from "@spiriidk/feature-flags";
import React, { memo, Suspense, useCallback, useMemo, useState } from "react";

import { ErrorFallback } from "~/components";

import { useBetaFeatures } from "../contexts";
import { useFilteredMenuItems, useMenuNavigation } from "../hooks";

import { FallbackLogo } from "./fallback-logo";
import { HelpMenuItem } from "./help-menu-item";
import { Logo } from "./logo";

const MemoizedLogo = memo(function MemoizedLogo() {
  return (
    <ErrorFallback fallback={<FallbackLogo />}>
      <Suspense fallback={<AvatarFallback>C</AvatarFallback>}>
        <Logo />
      </Suspense>
    </ErrorFallback>
  );
});

//TODO: Need to statically parse msg, find a better way to do this
function getTranslation(itemId: string) {
  const translations = {
    dashboard: msg`Dashboard`,
    locations: msg`Locations`,
    connectors: msg`Connectors`,
    "payment-terminals": msg`Payment Terminals`,
    "active-sessions": msg`Ongoing sessions`,
    "charge-records": msg`Charge Records`,
    "app-users": msg`App Users`,
    customers: msg`Customers`,
    reimbursements: msg`Reimbursements`,
    "charge-keys": msg`Charge Keys`,
    vouchers: msg`Vouchers`,
    tariffs: msg`Tariffs`,
    operations: msg`Operations`,
    settings: msg`Settings`,
    "settings-tabs": msg`Settings`,
    help: msg`Help`,
    "system-status": msg`All Systems Status`,
    "api-docs": msg`API docs`,
    updates: msg`Release notes`,
    "contact-support": msg`Contact Support`,
    "send-feedback": msg`Send Feedback`,
    "help-articles": msg`Help articles`,
    "debug-info": msg`Get debug info`,
    "user-profile": msg`Profile`,
    newsletter: msg`Newsletter`,
    support: msg`Support`,
    learning: msg`Learning`,
    exports: msg`Exports`,
    "team-access": msg`Team access`,
    "charge-map": msg`Network`,
    close: msg`Close`,
    Bookings: msg`Bookings`,
    "charge-boxes": msg`Charge boxes`,
    "access-requests": msg`Access Requests`,
    "chargepoint-operators": msg`Operators`,
  };

  return translations[itemId as keyof typeof translations] || itemId;
}

interface AppSidebarProps {
  onOpenImpersonateDialog: () => void;
  onOpenDebugInfoDialog: () => void;
}

function AppSidebar({
  onOpenImpersonateDialog,
  onOpenDebugInfoDialog,
}: AppSidebarProps) {
  const { open } = useSidebar();
  const {
    login,
    userPermissions,
    name,
    permittedOperatorId,
    permittedCompanyId,
  } = useAuth();
  const { _ } = useLingui();

  const { isImpersonating, canImpersonateOperator, canImpersonateCompany } =
    useImpersonation();

  const { isBetaEnabled } = useBetaFeatures();
  const isChargeMapEnabled = useFlag(FEATURE_FLAGS.CHARGE_MAP);
  const isSettingsEnabled = useFlag(FEATURE_FLAGS.SETTINGS);

  const canImpersonate = canImpersonateOperator || canImpersonateCompany;
  const isSpiriiAdminUser =
    permittedOperatorId === 0 && permittedCompanyId === 0 && !isImpersonating;

  const initials = name && typeof name === "string" ? name[0] : "";

  // Every flag the menu declarations reference, resolved once for all three lists.
  const flagValues = useMemo(
    () =>
      new Map<FeatureFlagConfiguration, boolean>([
        [FEATURE_FLAGS.CHARGE_MAP, isChargeMapEnabled],
        [FEATURE_FLAGS.SETTINGS, isSettingsEnabled],
      ]),
    [isChargeMapEnabled, isSettingsEnabled]
  );

  const filteredConnectMenuItems = useFilteredMenuItems({
    menuItems: connectMenuItems,
    userPermissions,
    isBetaEnabled,
    isSpiriiAdminUser,
    flagValues,
  });

  const filteredConnectFooterMenuItems = useFilteredMenuItems({
    menuItems: connectFooterMenuItems,
    userPermissions,
    isBetaEnabled,
    isSpiriiAdminUser,
    flagValues,
  });

  const filteredUserProfileMenuItems = useFilteredMenuItems({
    menuItems: connectUserProfileMenuItems,
    userPermissions,
    isBetaEnabled,
    isSpiriiAdminUser,
    flagValues,
  });

  const { handleNavigation, isActive } = useMenuNavigation();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const closeUserMenu = useCallback(() => setIsUserMenuOpen(false), []);

  const handleSignOut = useCallback(() => {
    void getKeycloakInstance().logout();
  }, []);

  return (
    <Sidebar>
      <SidebarHeader>
        <SidebarHeaderWorkspace>
          <a
            href="/"
            onClick={handleNavigation}
            className="ring-ring-default -m-1.5 flex flex-1 cursor-pointer items-center gap-3 rounded-lg p-1.5 outline-hidden focus-visible:ring-3"
            title={_(msg`Navigate to home page`)}
            data-track-name="home"
            data-track-label="Home"
            data-track-context="main-navigation"
          >
            <SidebarHeaderWorkspaceLogo>
              <MemoizedLogo />
            </SidebarHeaderWorkspaceLogo>
            <SidebarHeaderWorkspaceTitle>Connect</SidebarHeaderWorkspaceTitle>
          </a>
          <SidebarHeaderWorkspaceActions>
            {open && (
              <SidebarTrigger
                title={_(msg`Close`)}
                data-track-name="collapse-sidebar"
                data-track-label="Collapse sidebar"
                data-track-context="main-navigation"
              >
                <SidebarCloseIcon />
              </SidebarTrigger>
            )}
          </SidebarHeaderWorkspaceActions>
        </SidebarHeaderWorkspace>
      </SidebarHeader>
      <SidebarContent role="navigation" aria-label={_(msg`Main navigation`)}>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredConnectMenuItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton asChild isActive={isActive(item)}>
                      <a
                        href={item.url}
                        onClick={handleNavigation}
                        data-track-name={item.id.toLowerCase()}
                        data-track-context="main-navigation"
                      >
                        {IconComponent && <IconComponent />}
                        <span>{_(getTranslation(item.id))}</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          {filteredConnectFooterMenuItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <SidebarMenuItem key={item.id}>
                <SidebarMenuButton asChild isActive={isActive(item)}>
                  <a
                    href={item.url}
                    onClick={handleNavigation}
                    data-track-name={item.id.toLowerCase()}
                    data-track-context="main-navigation"
                  >
                    {IconComponent && <IconComponent />}
                    <span>{_(getTranslation(item.id))}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
          <SidebarMenuItem>
            <SidebarDropdownMenu>
              <SidebarDropdownMenuTrigger>
                <SidebarDropdownMenuButton
                  data-track-name="help-menu"
                  data-track-label="Help menu"
                  data-track-context="help-menu"
                >
                  {connectHelpMenuItem.icon && <connectHelpMenuItem.icon />}
                  <span>{_(getTranslation(connectHelpMenuItem.id))}</span>
                </SidebarDropdownMenuButton>
              </SidebarDropdownMenuTrigger>
              <SidebarDropdownMenuContent>
                {connectHelpMenuGroups.map((group, groupIndex) => (
                  <React.Fragment key={group.id}>
                    {groupIndex > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuGroup>
                      {group.labelId && (
                        <DropdownMenuLabel>
                          {_(getTranslation(group.labelId))}
                        </DropdownMenuLabel>
                      )}
                      {group.items.map((item) => (
                        <HelpMenuItem
                          key={item.id}
                          item={item}
                          label={_(getTranslation(item.id))}
                          onOpenDebugInfoDialog={onOpenDebugInfoDialog}
                        />
                      ))}
                    </DropdownMenuGroup>
                  </React.Fragment>
                ))}
              </SidebarDropdownMenuContent>
            </SidebarDropdownMenu>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarDropdownMenu
              open={isUserMenuOpen}
              onOpenChange={setIsUserMenuOpen}
            >
              <SidebarDropdownMenuTrigger>
                <SidebarUserProfile
                  className="group !pr-3"
                  // The e2e suite opens this menu to assert flag-gated items such
                  // as Team access; its visible content is the signed-in user's
                  // name, which varies per test.
                  data-testid="user-profile-menu-trigger"
                  data-track-name="user-menu"
                  data-track-label="User menu"
                  data-track-context="user-menu"
                >
                  <Avatar>
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                  <SidebarUserProfileInfo>
                    <SidebarUserProfileName>{name}</SidebarUserProfileName>
                    <SidebarUserProfileEmail>{login}</SidebarUserProfileEmail>
                  </SidebarUserProfileInfo>
                  <Icon
                    name="chevronDown"
                    size="sm"
                    className="text-shape-neutral-ghost-foreground-muted shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
                  />
                </SidebarUserProfile>
              </SidebarDropdownMenuTrigger>
              <SidebarDropdownMenuContent onClick={closeUserMenu}>
                {filteredUserProfileMenuItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <SidebarDropdownMenuItem key={item.id} asChild>
                      <a
                        href={item.url}
                        onClick={handleNavigation}
                        data-track-name={item.id.toLowerCase()}
                        data-track-context="user-menu"
                      >
                        {IconComponent && <IconComponent />}
                        <span>{_(getTranslation(item.id))}</span>
                      </a>
                    </SidebarDropdownMenuItem>
                  );
                })}
                {canImpersonate && (
                  <SidebarDropdownMenuItem
                    onClick={onOpenImpersonateDialog}
                    data-track-name="impersonate"
                    data-track-label="Impersonate"
                    data-track-context="user-menu"
                  >
                    <Icon name="drama" size="sm" />
                    <span>{_(msg`Impersonate`)}</span>
                  </SidebarDropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <SidebarDropdownMenuItem
                  onClick={handleSignOut}
                  data-track-name="sign-out"
                  data-track-label="Sign out"
                  data-track-context="user-menu"
                >
                  <Icon name="logOut" size="sm" />
                  <span>{_(msg`Sign out`)}</span>
                </SidebarDropdownMenuItem>
              </SidebarDropdownMenuContent>
            </SidebarDropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

export { AppSidebar };
